import { CommonModule } from '@angular/common';
import {
  Component,
  computed, inject,
  linkedSignal,
  model,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UserCalendarService } from './user-calendar.service';
import { forkJoin, lastValueFrom, Subject } from 'rxjs';
import { generateSampleCalendarNotes, ICalendarNote } from './user-calendar.model';
import { environment } from '../../../../environments/environment';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SharedModule } from '../../../Shared/shared.module';
import { SaveUserCalendarNoteComponent } from './save-user-calendar-note/save-user-calendar-note.component';
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarEventTimesChangedEvent,
  CalendarView,
  CalendarCommonModule,
  CalendarMonthModule,
  CalendarWeekModule,
  CalendarDayModule,
} from 'angular-calendar';
import { UtilityService } from '../../../Services/utility.service';
import {
  startOfDay,
  endOfDay, endOfMonth,
  isSameDay,
  isSameMonth, startOfMonth,
  endOfWeek,
  startOfWeek
} from 'date-fns';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { rxResource } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import { ViewUserCalendarNoteComponent } from './view-user-calendar-note/view-user-calendar-note.component';
import { ModalComponent } from '../../../Shared/components/modal/modal.component';
import { UserCalendarHelperService } from './user-calendar-helper.service';

@Component({
  selector: 'user-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    SharedModule,
    SaveUserCalendarNoteComponent,
    CalendarCommonModule,
    CalendarMonthModule,
    CalendarWeekModule,
    CalendarDayModule,
    MatButtonToggleModule,
    ViewUserCalendarNoteComponent,
  ],
  templateUrl: './user-calendar.component.html',
  styleUrl: './user-calendar.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class UserCalendarComponent implements OnInit { 
  readonly userCalendarHelperService = inject(UserCalendarHelperService);
  readonly selectedEvent = model<ICalendarNote>();
  readonly currentNote = signal<ICalendarNote>(null); 
  readonly view = signal<CalendarView>(CalendarView.Month);
  CalendarView = CalendarView;
  readonly viewDate = signal(new Date());
  activeDayIsOpen: boolean = true;
  readonly refresh = new Subject<void>();
  readonly currentViewDate = computed(
    () => {
      const d = this.getCurrentDateRange(this.view(), this.viewDate());
      console.log('getCurrentDateRange', d);
      return d;
    },
    { equal: (a, b) => isEqual(a, b) },
  );
  readonly viewNoteModalRef = viewChild<ModalComponent>('viewNoteModal');
  readonly saveNoteModalRef = viewChild<ModalComponent>('saveNoteModal');
  readonly calendarViews = Object.values(CalendarView);
  readonly eventsRes = rxResource({
    params: () => this.currentViewDate(),
    stream: (o) =>
      this.service.searchCalendarNotes({
        dateFrom: o.params.start.toISOString(),
        dateTo: o.params.end.toISOString(),
      }),
  });

  readonly loading = linkedSignal(() => this.eventsRes.isLoading());
  readonly notes = linkedSignal(() => this.eventsRes.value()?.content || []);
  readonly notesMap = computed(() => this.notes().toMap('id'));
  readonly events = computed(
    () =>
      this.notes().map<CalendarEvent>((x) => ({
        id: x.id,
        start: this.service.timeStringToDate(x.eventOn),
        title: x.title,
        actions: this.actions,
        color: {
          primary: this.userCalendarHelperService.typeToColour[x.eventType],
          secondary: this.userCalendarHelperService.typeToColour[x.eventType],
        },
      })) || [],
  );
  readonly actions: CalendarEventAction[] = [
    {
      label: '<i class="fas fa-fw fa-eye"></i>',
      a11yLabel: 'View',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.openEvent(event.id as number);
      },
    },
    {
      label: '<i class="fas fa-fw fa-pencil-alt"></i>',
      a11yLabel: 'Edit',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.currentNote.set(this.notesMap()[event.id]);
        this.saveNoteModalRef()?.open();
      },
    },
    {
      label: '<i class="fas fa-fw fa-trash-alt text-danger"></i>',
      a11yLabel: 'Delete',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.deleteEvent(event);
      },
    },
  ];

  constructor(
    public service: UserCalendarService,
    public uS: UtilityService,
  ) {
  }
  async ngOnInit(): Promise<void> {
    // concat(
    //   ...(await lastValueFrom(this.service.searchCalendarNotes({}).pipe(map((r) => r.content)))).map((n) =>
    //     this.service.deleteCalendarNote(n.id),
    //   ),
    // ).subscribe();
    const sampleCalendarNotes = generateSampleCalendarNotes();
    forkJoin(
      sampleCalendarNotes.map((n) =>
        this.service.saveCalendarNote({ ...n, userName: environment.userName }),
      ),
    ).subscribe();
  }

  openEvent(eventId: number | string) {
    this.currentNote.set(this.notesMap()[eventId]);
    this.viewNoteModalRef()?.open();
  }

  eventClicked($event: { event: CalendarEvent; sourceEvent: MouseEvent | KeyboardEvent }) {
    this.openEvent($event.event.id);
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    console.log(events);
    if (events.length === 0) {
      return;
    }
    if (isSameMonth(date, this.viewDate())) {
      if ((isSameDay(this.viewDate(), date) && this.activeDayIsOpen === true) || events.length === 0) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
    }
    this.viewDate.set(date);
  }

  setViewDate(viewDate: Date) {
    if (viewDate != this.viewDate()) this.viewDate.set(viewDate);
  }

  eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
    // this.events = this.events.map((iEvent) => {
    //   if (iEvent === event) {
    //     return { ...event, start: newStart, end: newEnd };
    //   }
    //   return iEvent;
    // });
  }

  refreshEvents(): void {
    this.eventsRes.reload();
  }

  async deleteEvent(event: CalendarEvent) {
    if (!event.id) return;
    try {
      this.notes.set(this.notes().filter((note) => note.id !== event.id));
      await lastValueFrom(this.service.deleteCalendarNote(event.id as number));
    } catch (error) {
      // debugger;
      this.refreshEvents();
      this.uS.info(error, 0);
    }
  }

  setView(view: CalendarView) {
    this.view.set(view);
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  // Get the current date range based on view
  getCurrentDateRange = (view: CalendarView, viewDate: Date) => {
    switch (view) {
      case CalendarView.Month:
        return { start: startOfMonth(viewDate), end: endOfMonth(viewDate) };
      case CalendarView.Week:
        return { start: startOfWeek(viewDate), end: endOfWeek(viewDate) };
      case CalendarView.Day:
        return { start: startOfDay(viewDate), end: endOfDay(viewDate) };
    }
  };
}
